package app.smartcoding.realrate.presentation.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.text.TextAutoSize
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import app.smartcoding.realrate.domain.model.CreditResult
import app.smartcoding.realrate.localization.LocalStrings
import app.smartcoding.realrate.presentation.util.evaluateCreditDealUseCase
import app.smartcoding.realrate.presentation.util.formatNumber

@Composable
fun Result1(
    result: CreditResult
) {
    val summaryExcellent = LocalStrings.current.summaryExcellent
    val summaryGood = LocalStrings.current.summaryGood
    val summaryFair = LocalStrings.current.summaryFair
    val summaryPoor = LocalStrings.current.summaryPoor
    val summaryBad = LocalStrings.current.summaryBad
    val detailsLowApr = LocalStrings.current.detailsLowApr
    val detailsHighApr = LocalStrings.current.detailsHighApr
    val detailsLowInterestCost = LocalStrings.current.detailsLowInterestCost
    val detailsHighInterestCost = LocalStrings.current.detailsHighInterestCost
    val detailsHighRateShortTerm = LocalStrings.current.detailsHighRateShortTerm
    val detailsGoodRateLongTerm = LocalStrings.current.detailsGoodRateLongTerm

    val evaluationResult by remember(result.effectiveApr, result.appreciation, LocalStrings.current) {
        derivedStateOf {
            evaluateCreditDealUseCase(
                apr = result.effectiveApr,
                appreciation = result.appreciation,
                summaryExcellent = summaryExcellent,
                summaryGood = summaryGood,
                summaryFair = summaryFair,
                summaryPoor = summaryPoor,
                summaryBad = summaryBad,
                detailsLowApr = detailsLowApr,
                detailsHighApr = detailsHighApr,
                detailsLowInterestCost = detailsLowInterestCost,
                detailsHighInterestCost = detailsHighInterestCost,
                detailsHighRateShortTerm = detailsHighRateShortTerm,
                detailsGoodRateLongTerm = detailsGoodRateLongTerm,
            )
        }
    }

    Box(
        modifier = Modifier.fillMaxSize()
    ) {
        val rating = evaluationResult.rating
        val effectiveApr = result.effectiveApr
        val appreciation = result.appreciation

        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp)
        )
        {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceContainerLow),
            ) {
                Box {
                    HeaderWaveBackground(
                        currentRate = effectiveApr,
                        currentAppreciation = appreciation,
                        modifier = Modifier.matchParentSize()
                    )
                    Column(modifier = Modifier.padding(horizontal = 16.dp, vertical = 16.dp)) {
                        Row(
                            verticalAlignment = Alignment.Top,
                            horizontalArrangement = Arrangement.SpaceBetween,
                        ) {
                            Text(
                                modifier = Modifier.padding(end = 8.dp).weight(1f),
                                text = rating.label,
                                style = MaterialTheme.typography.headlineMedium,
                                color = rating.color,
                                fontWeight = FontWeight.Bold,
                                maxLines = 1,
                                autoSize = TextAutoSize.StepBased(
                                    minFontSize = MaterialTheme.typography.bodySmall.fontSize,
                                    maxFontSize = MaterialTheme.typography.headlineMedium.fontSize
                                )

                            )
                            Box(contentAlignment = Alignment.Center) {
                                CircularProgressIndicator(
                                    progress = { evaluationResult.score.toFloat() / 100f },
                                    modifier = Modifier.size(56.dp),
                                    color = rating.color,
                                    trackColor = rating.color.copy(alpha = 0.2f),
                                    strokeWidth = 6.dp
                                )
                                Text(
                                    text = evaluationResult.score.toInt().toString(),
                                    style = MaterialTheme.typography.titleLarge,
                                    fontWeight = FontWeight.Bold,
                                    color = rating.color
                                )
                            }
                        }
                        Text(
                            text = evaluationResult.summary,
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 1,
                            autoSize = TextAutoSize.StepBased(
                                minFontSize = MaterialTheme.typography.bodySmall.fontSize,
                                maxFontSize = MaterialTheme.typography.bodyLarge.fontSize,
                            )
                        )
                        Text(
                            text = evaluationResult.details,
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 2,
                            autoSize = TextAutoSize.StepBased(
                                minFontSize = MaterialTheme.typography.bodySmall.fontSize,
                                maxFontSize = MaterialTheme.typography.bodyLarge.fontSize,
                            )
                        )
                    }
                }
            }

            Card(
                modifier = Modifier.fillMaxWidth().height(104.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surfaceContainerLow),
            ) {
                Column(
                    modifier = Modifier.padding(16.dp).fillMaxSize(),
                    verticalArrangement = Arrangement.Center
                ) {
                    // Key Metrics
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceAround
                    ) {
                        FinancialMetric(
                            text = LocalStrings.current.effectiveApr,
                            value = effectiveApr.formatNumber(),
                            highlightColor = if (effectiveApr > 15) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary
                        )
                        FinancialMetric(
                            text = LocalStrings.current.interest.uppercase(),
                            value = appreciation.formatNumber(),
                            highlightColor = if (appreciation > 50) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary
                        )
                    }
                }
            }
        }
    }
}